import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useApp } from './AppContext';
import { getCoachReply, getActionRecommendations } from '../services/aiService';

const AIContext = createContext(null);

function buildAIContextPayload(app) {
  return {
    userName: app.userName,
    region: app.region,
    footprint: app.footprint,
    doneActions: app.doneActions,
    remainingActions: app.items.filter((item) => !item.done),
    calculatorAnswers: app.calculatorAnswers,
  };
}

export function AIProvider({ children }) {
  const app = useApp();
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content:
        "Hi, I'm your GreenPlan AI coach. Ask me how to reduce your footprint, where to start, or what to do next.",
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [recommendations, setRecommendations] = useState([]);

  const askCoach = useCallback(
    async (question) => {
      const trimmed = question.trim();
      if (!trimmed || isLoading) return;

      setAiError('');
      setIsLoading(true);
      setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);

      try {
        const context = buildAIContextPayload(app);
        const answer = await getCoachReply(context, trimmed);
        setMessages((prev) => [...prev, { role: 'assistant', content: answer || 'I could not generate a response.' }]);
      } catch (error) {
        setAiError(error.message || 'AI request failed.');
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content:
              'I could not reach the AI service right now. Please check your API key and try again.',
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [app, isLoading]
  );

  const refreshRecommendations = useCallback(async () => {
    setAiError('');
    try {
      const context = buildAIContextPayload(app);
      const result = await getActionRecommendations(context);
      setRecommendations(result);
    } catch (error) {
      setAiError(error.message || 'Could not refresh recommendations.');
    }
  }, [app]);

  const clearChat = useCallback(() => {
    setMessages([
      {
        role: 'assistant',
        content:
          "Hi, I'm your GreenPlan AI coach. Ask me how to reduce your footprint, where to start, or what to do next.",
      },
    ]);
    setAiError('');
  }, []);

  const value = useMemo(
    () => ({
      messages,
      isLoading,
      aiError,
      recommendations,
      askCoach,
      clearChat,
      refreshRecommendations,
    }),
    [messages, isLoading, aiError, recommendations, askCoach, clearChat, refreshRecommendations]
  );

  return <AIContext.Provider value={value}>{children}</AIContext.Provider>;
}

export function useAI() {
  const context = useContext(AIContext);
  if (!context) {
    throw new Error('useAI must be used within an AIProvider');
  }
  return context;
}
